import React, { useState, useRef } from 'react';
import {
  Box, Button, Card, CardContent, Drawer, Grid, IconButton, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography, Alert, CircularProgress, Stack, Chip, Tooltip, Divider,
  FormControl, InputLabel, Select, MenuItem, TextField, Pagination,
  InputAdornment, Collapse, useTheme, useMediaQuery,
  Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Backdrop,
  LinearProgress, alpha,
} from '@mui/material';
import {
  Add as AddIcon, Delete as DeleteIcon, Assessment as AssessmentIcon,
  PictureAsPdf as PdfIcon, Refresh as RefreshIcon, Visibility as VisibilityIcon,
  FilterList as FilterListIcon, Search as SearchIcon, Clear as ClearIcon,
  ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon, Close as CloseIcon,
  Cancel as CancelIcon, CheckCircle as CheckCircleIcon, Sync as SyncIcon,
  ErrorOutline as ErrorOutlineIcon,
} from '@mui/icons-material';
import { Report, ReportType, ReportFormData, ReportFilters } from './Report';
import { useAuth } from '../../../contexts/AuthContext';
import { ReportPDFGenerator } from './reportPDFGenerator';
import ReportDetailsDialog from './ReportDetailsDialog';
import { useReportsPaginated, useReportStats, useReportMutations } from '../../../hooks/useReports';

const REPORTS_PER_PAGE = 10;
const MAX_RANGE_DAYS = 90;

const DATE_FILTER_SUPPORTED: ReportType[] = [
  'sales', 'purchase_orders', 'product_performance', 'revenue', 'returns_cancellations', 'profit_loss',
];

// Report types where the date range is not optional — a recurring cost can
// only be prorated against a bounded period, so Profit & Loss needs both ends.
const REQUIRES_DATE_RANGE: ReportType[] = ['profit_loss'];

const REPORT_TYPES = [
  { value: 'sales', label: 'Sales Report', description: 'Every vehicle sold — buyer, price, payment method and status' },
  { value: 'inventory', label: 'Inventory Report', description: 'Vehicles currently in stock, reserved or unavailable, by branch' },
  { value: 'purchase_orders', label: 'Import Orders Report', description: 'Supplier import order analysis' },
  { value: 'low_stock', label: 'Reserved & Incoming Vehicles', description: 'Vehicles that are reserved or still in transit — about to become unavailable to sell' },
  { value: 'warehouse_performance', label: 'Show Room Performance', description: 'Show room capacity, utilization and vehicle availability' },
  { value: 'product_performance', label: 'Model Performance', description: 'Which vehicle models sell best — units sold, revenue, and stock turnover' },
  { value: 'revenue', label: 'Revenue Report', description: 'Daily revenue trends and top-performing models from sold vehicles' },
  { value: 'returns_cancellations', label: 'Cancelled & Lost Deals Report', description: 'Reservations that were cancelled or went cold before becoming a sale' },
  { value: 'profit_loss', label: 'Profit & Loss Report', description: 'Revenue, cost of goods sold, operating costs (incl. recurring) and net profit for a date range' },
];

const getSoftChipSx = (color: string, isDark: boolean = false) => {
  type PaletteEntry = { bg: string; border: string; text: string; iconColor: string };

  const lightPalette: Record<string, PaletteEntry> = {
    warning: { bg: 'rgba(255,167,38,0.12)', border: 'rgba(255,167,38,0.4)', text: '#b76e00', iconColor: '#e68900' },
    info: { bg: 'rgba(41,182,246,0.12)', border: 'rgba(41,182,246,0.4)', text: '#0277bd', iconColor: '#039be5' },
    primary: { bg: 'rgba(33,150,243,0.12)', border: 'rgba(33,150,243,0.4)', text: '#1565c0', iconColor: '#1976d2' },
    success: { bg: 'rgba(102,187,106,0.15)', border: 'rgba(102,187,106,0.45)', text: '#2e7d32', iconColor: '#43a047' },
    error: { bg: 'rgba(239,83,80,0.12)', border: 'rgba(239,83,80,0.4)', text: '#c62828', iconColor: '#e53935' },
    secondary: { bg: 'rgba(171,71,188,0.10)', border: 'rgba(171,71,188,0.35)', text: '#6a1b9a', iconColor: '#8e24aa' },
    default: { bg: 'rgba(158,158,158,0.12)', border: 'rgba(158,158,158,0.4)', text: '#424242', iconColor: '#757575' },
  };

  const darkPalette: Record<string, PaletteEntry> = {
    warning: { bg: 'rgba(255,167,38,0.22)', border: 'rgba(255,167,38,0.55)', text: '#ffcc80', iconColor: '#ffa726' },
    info: { bg: 'rgba(41,182,246,0.20)', border: 'rgba(41,182,246,0.50)', text: '#81d4fa', iconColor: '#29b6f6' },
    primary: { bg: 'rgba(33,150,243,0.22)', border: 'rgba(33,150,243,0.50)', text: '#90caf9', iconColor: '#42a5f5' },
    success: { bg: 'rgba(102,187,106,0.22)', border: 'rgba(102,187,106,0.50)', text: '#a5d6a7', iconColor: '#66bb6a' },
    error: { bg: 'rgba(239,83,80,0.22)', border: 'rgba(239,83,80,0.50)', text: '#ef9a9a', iconColor: '#ef5350' },
    secondary: { bg: 'rgba(171,71,188,0.22)', border: 'rgba(171,71,188,0.50)', text: '#ce93d8', iconColor: '#ab47bc' },
    default: { bg: 'rgba(158,158,158,0.22)', border: 'rgba(158,158,158,0.50)', text: '#e0e0e0', iconColor: '#bdbdbd' },
  };

  const palette = isDark ? darkPalette : lightPalette;
  const p = palette[color] ?? palette.default;

  return {
    backgroundColor: p.bg, borderColor: p.border, color: p.text, fontWeight: 600,
    border: `1px solid ${p.border}`,
    '& .MuiChip-icon': { color: p.iconColor },
    '& .MuiChip-label': { color: p.text },
  };
};

const getStatusChipColor = (s: string) =>
  s === 'completed' ? 'success' : s === 'generating' ? 'warning' : s === 'failed' ? 'error' : 'default';

const getStatusLabel = (s: string) =>
  s === 'completed' ? 'Completed' : s === 'generating' ? 'Generating' : s === 'failed' ? 'Failed' : s;

const todayStr = (): string => new Date().toISOString().split('T')[0];

const addDays = (date: Date, days: number): string => {
  const d = new Date(date.getTime());
  d.setDate(d.getDate() + days);
  const today = new Date();
  if (days > 0 && d > today) return today.toISOString().split('T')[0];
  return d.toISOString().split('T')[0];
};

const daysDiff = (a: Date, b: Date): number =>
  Math.round((b.getTime() - a.getTime()) / 86_400_000);

interface LoadingOverlayProps {
  wrapperRef: React.RefObject<HTMLDivElement | null>;
}

const LoadingOverlay: React.FC<LoadingOverlayProps> = ({ wrapperRef }) => {
  const [rect, setRect] = useState<DOMRect | null>(null);
  const theme = useTheme();
  const overlayBg = theme.palette.mode === 'dark'
    ? 'rgba(26, 29, 36, 0.85)'
    : 'rgba(255, 255, 255, 0.85)';

  React.useEffect(() => {
    const measure = () => {
      if (wrapperRef.current) setRect(wrapperRef.current.getBoundingClientRect());
    };
    measure();
    const el = wrapperRef.current;
    window.addEventListener('resize', measure);
    el?.addEventListener('scroll', measure);
    return () => {
      window.removeEventListener('resize', measure);
      el?.removeEventListener('scroll', measure);
    };
  }, [wrapperRef]);

  if (!rect) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        top: rect.top, left: rect.left,
        width: rect.width, height: rect.height,
        backgroundColor: overlayBg,
        backdropFilter: 'blur(2px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1100, pointerEvents: 'all', borderRadius: 'inherit',
      }}
    >
      <Box sx={{ textAlign: 'center' }}>
        <CircularProgress size={40} />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Loading reports...
        </Typography>
      </Box>
    </Box>
  );
};

const ReportManagement: React.FC = () => {
  const { currentUser, hasPermission, isSuperAdmin } = useAuth();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // ── UI state ──────────────────────────────────────────────────────────────
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState<Report | null>(null);
  const [deleteLoading, setDeleteLoading] = useState<string>('');
  const [downloadingPdf, setDownloadingPdf] = useState<string>('');
  const [deleteTarget, setDeleteTarget] = useState<Report | null>(null);
  const [deleteDialogLoading, setDeleteDialogLoading] = useState(false);
  const [dateError, setDateError] = useState('');
  const [formData, setFormData] = useState<ReportFormData>({ reportType: 'sales', reportName: '', description: '', filters: {} });
  const [filters, setFilters] = useState<ReportFilters>({});

  // ── Pagination & filter state ─────────────────────────────────────────────
  // FIX: Removed all cursor state (lastVisible, firstVisible, cursors).
  // Seek-based pagination only needs the current page number.
  const [currentPage, setCurrentPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showFilters, setShowFilters] = useState(false);

  const canView = isSuperAdmin || hasPermission('reports.view');
  const canCreate = isSuperAdmin || hasPermission('reports.create');
  const canDelete = isSuperAdmin || hasPermission('reports.delete');
  const canDownload = isSuperAdmin || hasPermission('reports.download');

  const tableWrapperRef = useRef<HTMLDivElement>(null);

  const showDateFilter = DATE_FILTER_SUPPORTED.includes(formData.reportType as ReportType);

  // ── React Query hooks ─────────────────────────────────────────────────────
  // FIX: No longer passes a `cursors` object — just page number + filters.
  const {
    data: reportsData,
    isLoading: reportsLoading,
    isFetching: reportsIsFetching,
    isError: reportsError,
    error: reportsErrorObj,
  } = useReportsPaginated({
    page: currentPage,
    search: searchQuery,
    typeFilter,
    statusFilter,
    pageSize: REPORTS_PER_PAGE,
  });

  const { data: statsData } = useReportStats();
  const { generate, remove } = useReportMutations();

  // ── Derived values ────────────────────────────────────────────────────────
  const reports = reportsData?.reports ?? [];
  const totalReports = reportsData?.totalCount ?? 0;
  const totalPages = Math.ceil(totalReports / REPORTS_PER_PAGE);
  const loading = reportsLoading || reportsIsFetching;

  const totalCompleted = statsData?.completed ?? 0;
  const totalGenerating = statsData?.generating ?? 0;
  const totalFailed = statsData?.failed ?? 0;

  React.useEffect(() => {
    if (!showDateFilter) {
      setFilters(prev => ({ ...prev, dateFrom: undefined, dateTo: undefined }));
      setDateError('');
    }
  }, [formData.reportType, showDateFilter]);

  // ── Reset to page 1 ───────────────────────────────────────────────────────
  // FIX: Simplified — no cursor state to reset, just set page to 1.
  const resetToFirstPage = () => {
    setCurrentPage(1);
  };

  // ── Pagination handler ────────────────────────────────────────────────────
  // FIX: Simplified — just set the page number directly. The seek-based
  // pagination in the service computes the correct Firestore offset from
  // the page number, so jumping to any page works correctly.
  const handlePageChange = (_: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
  };

  // ── Search ────────────────────────────────────────────────────────────────
  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setSearchQuery(searchInput);
      resetToFirstPage();
    }
  };

  // ── Filters ───────────────────────────────────────────────────────────────
  const handleTypeFilterChange = (v: string) => {
    setTypeFilter(v);
    resetToFirstPage();
  };

  const handleStatusFilterChange = (v: string) => {
    setStatusFilter(v);
    resetToFirstPage();
  };

  const clearFilters = () => {
    setSearchInput('');
    setSearchQuery('');
    setTypeFilter('all');
    setStatusFilter('all');
    resetToFirstPage();
  };

  const hasActiveFilters = searchQuery !== '' || typeFilter !== 'all' || statusFilter !== 'all';
  const activeFiltersCount = [searchQuery !== '', typeFilter !== 'all', statusFilter !== 'all'].filter(Boolean).length;

  // ── Form helpers ──────────────────────────────────────────────────────────
  const resetForm = () => {
    setFormData({ reportType: 'sales', reportName: '', description: '', filters: {} });
    setFilters({});
    setDateError('');
  };

  const handleAddReport = () => { resetForm(); setDrawerOpen(true); };
  const handleCloseDrawer = () => { setDrawerOpen(false); resetForm(); };
  const handleViewReport = (report: Report) => { setSelectedReport(report); setDetailsDialogOpen(true); };

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDeleteReport = (report: Report) => setDeleteTarget(report);
  const handleDeleteCancel = () => setDeleteTarget(null);

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      setDeleteDialogLoading(true);
      setDeleteLoading(deleteTarget.id!);
      const result = await remove.mutateAsync(deleteTarget.id!);
      if (result.success) {
        setDeleteTarget(null);
        resetToFirstPage();
      } else {
        alert(result.message || 'Failed to delete report');
      }
    } catch (err: any) {
      alert('Failed to delete report: ' + (err.message || 'Unknown error'));
    } finally {
      setDeleteDialogLoading(false);
      setDeleteLoading('');
    }
  };

  // ── Generate report ───────────────────────────────────────────────────────
  const handleGenerateReport = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.reportName.trim()) { alert('Report name is required'); return; }
    if (dateError) { alert('Please fix the date range error before generating.'); return; }
    if (REQUIRES_DATE_RANGE.includes(formData.reportType) && (!filters.dateFrom || !filters.dateTo)) {
      alert('This report type requires both a start and end date.');
      return;
    }
    try {
      await generate.mutateAsync({
        reportType: formData.reportType,
        reportName: formData.reportName,
        description: formData.description,
        filters,
        userId: currentUser?.uid || '',
        userName: currentUser?.displayName || currentUser?.email || 'Unknown User',
      });
      resetToFirstPage();
      setDrawerOpen(false);
      resetForm();
    } catch (err: any) {
      alert('Failed to generate report: ' + (err.message || 'Unknown error'));
    }
  };

  const generating = generate.isPending;

  // ── PDF download ──────────────────────────────────────────────────────────
  const handleDownloadPDF = async (report: Report) => {
    try {
      setDownloadingPdf(report.id!);
      await ReportPDFGenerator.generatePDF(report);
    } catch (err: any) {
      alert('Failed to download PDF: ' + (err.message || 'Unknown error'));
    } finally {
      setDownloadingPdf('');
    }
  };

  const handleRefresh = () => resetToFirstPage();

  // ── Date handlers ─────────────────────────────────────────────────────────
  const handleDateFromChange = (value: string) => {
    if (!value) { setFilters(prev => ({ ...prev, dateFrom: undefined })); setDateError(''); return; }
    const newFrom = new Date(value);
    setFilters(prev => {
      if (prev.dateTo) {
        const diff = daysDiff(newFrom, prev.dateTo);
        if (diff < 0) setDateError('Start date cannot be after end date.');
        else if (diff > MAX_RANGE_DAYS) setDateError(`Date range cannot exceed ${MAX_RANGE_DAYS} days.`);
        else setDateError('');
      } else { setDateError(''); }
      return { ...prev, dateFrom: newFrom };
    });
  };

  const handleDateToChange = (value: string) => {
    if (!value) { setFilters(prev => ({ ...prev, dateTo: undefined })); setDateError(''); return; }
    const newTo = new Date(value);
    // `value` is a plain YYYY-MM-DD string, which Date parses as midnight —
    // the very start of that day. Push it to the last instant of the day so
    // sales made any time on the end date (including today) are included by
    // the '<=' comparisons in reportService.ts instead of being excluded.
    newTo.setHours(23, 59, 59, 999);
    setFilters(prev => {
      if (prev.dateFrom) {
        const diff = daysDiff(prev.dateFrom, newTo);
        if (diff < 0) setDateError('End date cannot be before start date.');
        else if (diff > MAX_RANGE_DAYS) setDateError(`Date range cannot exceed ${MAX_RANGE_DAYS} days.`);
        else setDateError('');
      } else { setDateError(''); }
      return { ...prev, dateTo: newTo };
    });
  };

  if (!canView) {
    return (
      <Box sx={{ p: 3 }}>
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <AssessmentIcon sx={{ fontSize: 64, color: 'warning.main', mb: 2, opacity: 0.7 }} />
            <Typography variant="h5" gutterBottom>Access Denied</Typography>
            <Typography variant="body1" color="text.secondary">
              You don't have permission to view reports. Please contact your administrator.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  const dateFromValue = filters.dateFrom ? filters.dateFrom.toISOString().split('T')[0] : '';
  const dateToValue = filters.dateTo ? filters.dateTo.toISOString().split('T')[0] : '';
  const dateFromMin = filters.dateTo ? addDays(filters.dateTo, -MAX_RANGE_DAYS) : undefined;
  const dateFromMax = filters.dateTo ? dateToValue : todayStr();
  const dateToMin = filters.dateFrom ? dateFromValue : undefined;
  const dateToMax = filters.dateFrom ? addDays(filters.dateFrom, MAX_RANGE_DAYS) : todayStr();

  const getReportTypeLabel = (type: ReportType) =>
    REPORT_TYPES.find(rt => rt.value === type)?.label || type;

  if (loading && reports.length === 0) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight="400px">
        <CircularProgress size={40} />
        <Typography variant="body1" sx={{ ml: 2 }}>Loading reports...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: { md: '100%' }, display: 'flex', flexDirection: 'column', overflow: { xs: 'visible', md: 'hidden' }, p: { xs: 2, sm: 3 } }}>

      <Box sx={{ flexShrink: 0 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2} sx={{ mb: 3 }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <AssessmentIcon sx={{ fontSize: { xs: 28, md: 32 }, color: 'primary.main' }} />
            <Box>
              <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.5rem', md: '2rem' } }}>
                Report Management
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Generate and manage business reports
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1} sx={{ alignSelf: { xs: 'flex-start', sm: 'auto' } }}>
            <Button onClick={handleRefresh} variant="outlined" startIcon={<RefreshIcon />} size={isMobile ? 'small' : 'medium'} disabled={loading}>
              {isMobile ? '' : 'Refresh'}
            </Button>
            {canCreate && (
              <Button onClick={handleAddReport} variant="contained" startIcon={<AddIcon />} size={isMobile ? 'small' : 'medium'}>
                Generate Report
              </Button>
            )}
          </Stack>
        </Stack>

        {/* {reportsError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {(reportsErrorObj as Error)?.message || 'Failed to fetch reports'}
          </Alert>
        )} */}

        {/* ── Stats Cards ── */}
        <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }} sx={{ mb: 3 }} alignItems="stretch">
          {[
            {
              label: 'Total Reports', value: totalReports, icon: <AssessmentIcon fontSize="small" />,
              sub: (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'success.main', flexShrink: 0 }} />
                    <Typography variant="caption" color="success.main" noWrap>{totalCompleted} completed</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'error.main', flexShrink: 0 }} />
                    <Typography variant="caption" color="error.main" noWrap>{totalFailed} failed</Typography>
                  </Box>
                </>
              ),
            },
            {
              label: 'Completed', value: totalCompleted, icon: <CheckCircleIcon fontSize="small" />,
              sub: <Typography variant="caption" color="text.secondary" noWrap>{totalReports > 0 ? `${((totalCompleted / totalReports) * 100).toFixed(1)}% of total` : '—'}</Typography>,
            },
            {
              label: 'Generating', value: totalGenerating, icon: <SyncIcon fontSize="small" />,
              sub: <Typography variant="caption" color="text.secondary" noWrap>{totalGenerating > 0 ? 'currently in progress' : 'none in progress'}</Typography>,
            },
            {
              label: 'Failed', value: totalFailed, icon: <ErrorOutlineIcon fontSize="small" />,
              sub: <Typography variant="caption" color="text.secondary" noWrap>{totalReports > 0 ? `${((totalFailed / totalReports) * 100).toFixed(1)}% failure rate` : '—'}</Typography>,
            },
          ].map((card, i) => (
            <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }} key={i}>
              <Card
                sx={{
                  width: '100%', display: 'flex', flexDirection: 'column',
                  border: `1px solid ${theme.palette.divider}`,
                  transition: 'box-shadow 0.15s ease',
                  '&:hover': { boxShadow: 4 },
                }}
              >
                <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } }, flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                    <Typography color="text.secondary" variant="body2" noWrap sx={{ fontWeight: 600 }}>{card.label}</Typography>
                    <Box
                      sx={{
                        width: 28, height: 28, borderRadius: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        bgcolor: alpha(theme.palette.text.primary, isDark ? 0.12 : 0.06), color: 'text.secondary', flexShrink: 0,
                      }}
                    >
                      {card.icon}
                    </Box>
                  </Stack>
                  <Typography variant="h5" component="div" color="text.primary" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' }, mb: 1, fontWeight: 700 }}>
                    {card.value}
                  </Typography>
                  <Box sx={{ mt: 'auto', display: 'flex', flexDirection: 'column', gap: 0.5 }}>{card.sub}</Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* ── Search & Filters ── */}
        <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 2 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between">
            <TextField
              placeholder="Search reports by name... (Press Enter)"
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              onKeyPress={handleSearchKeyPress}
              sx={{ flexGrow: 1, maxWidth: { sm: 400 } }}
              size="small"
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
            />
            <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
              <Button
                onClick={() => setShowFilters(!showFilters)}
                variant={hasActiveFilters ? 'contained' : 'outlined'}
                startIcon={<FilterListIcon />}
                endIcon={showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                size="small"
              >
                Filters {hasActiveFilters && `(${activeFiltersCount})`}
              </Button>
              {hasActiveFilters && (
                <Button onClick={clearFilters} variant="text" size="small" startIcon={<ClearIcon />}>
                  Clear all
                </Button>
              )}
            </Stack>
          </Stack>

          <Collapse in={showFilters}>
            <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Report Type</InputLabel>
                    <Select value={typeFilter} onChange={e => handleTypeFilterChange(e.target.value)} label="Report Type">
                      <MenuItem value="all">All Types</MenuItem>
                      {REPORT_TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Status</InputLabel>
                    <Select value={statusFilter} onChange={e => handleStatusFilterChange(e.target.value)} label="Status">
                      <MenuItem value="all">All Status</MenuItem>
                      <MenuItem value="completed">Completed</MenuItem>
                      <MenuItem value="generating">Generating</MenuItem>
                      <MenuItem value="failed">Failed</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', height: '40px' }}>
                    <Typography variant="body2" color="text.secondary">
                      {hasActiveFilters
                        ? `Found ${reports.length} reports matching your filters`
                        : `Showing ${reports.length} reports on this page`}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Collapse>
        </Paper>
      </Box>

      {/* ── Scrollable Body ── */}
      <Box sx={{ flex: 1, overflow: { xs: 'visible', md: 'auto' }, display: 'flex', flexDirection: 'column', minHeight: { xs: 'auto', md: 0 } }}>

        {reports.length === 0 && !loading && (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
            <Card sx={{ width: '100%', maxWidth: 500 }}>
              <CardContent sx={{ textAlign: 'center', py: 6 }}>
                <AssessmentIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
                <Typography variant="h5" color="text.secondary" gutterBottom>
                  {hasActiveFilters ? 'No Reports Match Your Filters' : 'No Reports Generated'}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}>
                  {hasActiveFilters
                    ? 'Try adjusting your search or filter criteria.'
                    : 'Start by generating your first business report.'}
                </Typography>
                {hasActiveFilters
                  ? <Button onClick={clearFilters} variant="outlined" startIcon={<ClearIcon />}>Clear Filters</Button>
                  : canCreate && <Button onClick={handleAddReport} variant="contained" startIcon={<AddIcon />}>Generate Report</Button>}
              </CardContent>
            </Card>
          </Box>
        )}

        {reports.length > 0 && (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: { xs: 'auto', md: 0 } }}>
            <TableContainer
              component={Paper}
              ref={tableWrapperRef}
              sx={{
                flex: 1, overflow: { xs: 'auto', md: 'auto' }, maxHeight: { md: '100%' }, mb: 2, position: 'relative',
                '& .MuiTable-root': { minWidth: { xs: 650, md: 1000 } },
              }}
            >
              <Table stickyHeader size={isMobile ? 'small' : 'medium'}>
                <TableHead>
                  <TableRow>
                    <TableCell>Report Name</TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Type</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Generated By</TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Generated At</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Records</TableCell>
                    {(canView || canCreate || canDelete || canDownload) && (
                      <TableCell align="right">Actions</TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reports.map(report => (
                    <TableRow key={report.id} hover sx={{ '&:last-child td, &:last-child th': { borderBottom: 0 } }}>
                      <TableCell>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: { xs: '0.85rem', sm: '1rem' } }}>
                          {report.reportName}
                        </Typography>
                        {report.description && (
                          <Typography variant="caption" color="text.secondary" display="block">{report.description}</Typography>
                        )}
                        <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'block', sm: 'none' } }}>
                          {getReportTypeLabel(report.reportType)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        <Chip label={getReportTypeLabel(report.reportType)} size="small" variant="outlined" sx={getSoftChipSx('primary', isDark)} />
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        <Typography variant="body2">{report.generatedByName}</Typography>
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        <Typography variant="body2" color="text.secondary">
                          {report.generatedAt
                            ? new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'short', day: 'numeric' }).format(report.generatedAt)
                            : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={getStatusLabel(report.status)} size="small" variant="outlined" sx={getSoftChipSx(getStatusChipColor(report.status), isDark)} />
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        <Typography variant="body2">{report.summary?.totalRecords || 0}</Typography>
                      </TableCell>
                      {(canView || canCreate || canDelete || canDownload) && (
                        <TableCell align="right">
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end" flexWrap="nowrap">
                            {report.status === 'completed' && (
                              <>
                                {canView && (
                                  <Tooltip title="View Details">
                                    <IconButton onClick={() => handleViewReport(report)} size="small" color="primary">
                                      <VisibilityIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                {canDownload && (
                                  <Tooltip title="Download PDF">
                                    <IconButton onClick={() => handleDownloadPDF(report)} disabled={downloadingPdf === report.id} size="small" color="secondary">
                                      {downloadingPdf === report.id ? <CircularProgress size={16} /> : <PdfIcon fontSize="small" />}
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </>
                            )}
                            {canDelete && (
                              <Tooltip title="Delete">
                                <IconButton onClick={() => handleDeleteReport(report)} disabled={deleteLoading === report.id} size="small" color="error">
                                  {deleteLoading === report.id ? <CircularProgress size={16} /> : <DeleteIcon fontSize="small" />}
                                </IconButton>
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {loading && <LoadingOverlay wrapperRef={tableWrapperRef} />}

            <Paper elevation={2} sx={{ flexShrink: 0, borderRadius: 1 }}>
              <Box sx={{ py: 2, px: 2 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1.5, sm: 3 }} alignItems="center" justifyContent="space-between" sx={{ width: '100%' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    Showing {totalReports === 0 ? 0 : (currentPage - 1) * REPORTS_PER_PAGE + 1}–
                    {Math.min(currentPage * REPORTS_PER_PAGE, totalReports)} of {totalReports} reports
                  </Typography>
                  <Pagination
                    count={Math.max(1, totalPages)} page={currentPage} onChange={handlePageChange}
                    color="primary" size={isMobile ? 'small' : 'medium'} showFirstButton showLastButton
                    disabled={loading || totalPages <= 1}
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    Page {currentPage} of {Math.max(1, totalPages)}
                  </Typography>
                </Stack>
              </Box>
            </Paper>
          </Box>
        )}
      </Box>

      {/* ── Generate Report Drawer ── */}
      <Drawer anchor="right" open={drawerOpen} onClose={handleCloseDrawer} PaperProps={{ sx: { width: { xs: '100%', sm: 560 } } }}>
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ px: 3, py: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
            <Typography variant="h6">Generate New Report</Typography>
            <IconButton onClick={handleCloseDrawer} edge="end"><CloseIcon /></IconButton>
          </Box>

          <Box component="form" onSubmit={handleGenerateReport} sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
            <Box sx={{ flex: 1, overflow: 'auto', px: 3, py: 3, minHeight: 0, '&::-webkit-scrollbar': { width: '6px' }, '&::-webkit-scrollbar-track': { background: 'transparent' }, '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.2)', borderRadius: '3px' }, '&::-webkit-scrollbar-thumb:hover': { background: 'rgba(0,0,0,0.35)' } }}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Report Type</InputLabel>
                    <Select value={formData.reportType} label="Report Type" onChange={e => setFormData(prev => ({ ...prev, reportType: e.target.value as ReportType }))}>
                      {REPORT_TYPES.map(t => (
                        <MenuItem key={t.value} value={t.value}>
                          <Box>
                            <Typography variant="body1">{t.label}</Typography>
                            <Typography variant="caption" color="text.secondary">{t.description}</Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth label="Report Name" required
                    value={formData.reportName}
                    onChange={e => setFormData(prev => ({ ...prev, reportName: e.target.value }))}
                    placeholder="e.g., Monthly Sales Report - February 2026"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth label="Description" multiline rows={2}
                    value={formData.description}
                    onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Optional description for this report"
                  />
                </Grid>

                {showDateFilter && (
                  <>
                    <Grid item xs={12}>
                      <Divider sx={{ mb: 2 }} />
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                        <FilterListIcon color="primary" fontSize="small" />
                        <Typography variant="subtitle1" fontWeight={600}>Date Filter</Typography>
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        {REQUIRES_DATE_RANGE.includes(formData.reportType)
                          ? 'Required for this report — maximum range: 90 days.'
                          : 'Maximum range: 90 days. Leave blank to include all dates.'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth required={REQUIRES_DATE_RANGE.includes(formData.reportType)} label="Date From" type="date" value={dateFromValue} onChange={e => handleDateFromChange(e.target.value)} InputLabelProps={{ shrink: true }} inputProps={{ min: dateFromMin, max: dateFromMax }} error={!!dateError} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth required={REQUIRES_DATE_RANGE.includes(formData.reportType)} label="Date To" type="date" value={dateToValue} onChange={e => handleDateToChange(e.target.value)} InputLabelProps={{ shrink: true }} inputProps={{ min: dateToMin, max: dateToMax }} error={!!dateError} />
                    </Grid>
                    {dateError && (
                      <Grid item xs={12}>
                        <Alert severity="error" sx={{ py: 0.5 }}>{dateError}</Alert>
                      </Grid>
                    )}
                  </>
                )}
              </Grid>
            </Box>

            <Divider />
            <Box sx={{ px: 3, py: 2, display: 'flex', gap: 2, justifyContent: 'flex-end', flexShrink: 0 }}>
              <Button onClick={handleCloseDrawer} disabled={generating} startIcon={<CancelIcon />}>Cancel</Button>
              <Button type="submit" variant="contained" disabled={generating || !!dateError} startIcon={generating ? <CircularProgress size={16} /> : <AssessmentIcon />}>
                {generating ? 'Generating...' : 'Generate Report'}
              </Button>
            </Box>
          </Box>
        </Box>
      </Drawer>

      {/* ── Delete Confirmation Dialog ── */}
      <Dialog open={!!deleteTarget} onClose={handleDeleteCancel} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Report</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete <strong>"{deleteTarget?.reportName}"</strong>? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleDeleteCancel} disabled={deleteDialogLoading}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteConfirm} disabled={deleteDialogLoading} startIcon={deleteDialogLoading ? <CircularProgress size={16} /> : <DeleteIcon />}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {selectedReport && (
        <ReportDetailsDialog
          open={detailsDialogOpen}
          report={selectedReport}
          onClose={() => { setDetailsDialogOpen(false); setSelectedReport(null); }}
        />
      )}
      {/* ── Full-screen operation overlay ───────────────────────────────────── */}
      <Backdrop
        open={generating || deleteDialogLoading}
        sx={{
          zIndex: (t) => t.zIndex.drawer + 200,
          flexDirection: 'column',
          gap: 3,
          bgcolor: 'rgba(0,0,0,0.6)',
        }}
      >
        <Box
          sx={{
            bgcolor: 'background.paper',
            borderRadius: 3,
            px: 5,
            py: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2.5,
            minWidth: 280,
            boxShadow: 24,
          }}
        >
          <CircularProgress size={48} thickness={4} />
          <Box sx={{ width: '100%' }}>
            <LinearProgress sx={{ borderRadius: 1, mb: 1.5 }} />
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 600, textAlign: 'center', color: 'text.primary' }}
            >
              {deleteDialogLoading ? 'Deleting Report…' : 'Generating Report…'}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', textAlign: 'center', mt: 0.5 }}
            >
              Please wait, do not close this window
            </Typography>
          </Box>
        </Box>
      </Backdrop>
    </Box>
  );
};

export default ReportManagement;

// import React, { useState, useEffect, useRef } from 'react';
// import {
//   Box, Button, Card, CardContent, Drawer, Grid, IconButton, Paper,
//   Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
//   Typography, Alert, CircularProgress, Stack, Chip, Tooltip, Divider,
//   FormControl, InputLabel, Select, MenuItem, TextField, Pagination,
//   InputAdornment, Collapse, useTheme, useMediaQuery,
//   Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
// } from '@mui/material';
// import {
//   Add as AddIcon, Delete as DeleteIcon, Assessment as AssessmentIcon,
//   PictureAsPdf as PdfIcon, Refresh as RefreshIcon, Visibility as VisibilityIcon,
//   FilterList as FilterListIcon, Search as SearchIcon, Clear as ClearIcon,
//   ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon, Close as CloseIcon,
//   Cancel as CancelIcon,
// } from '@mui/icons-material';
// import { Report, ReportType, ReportFormData, ReportFilters } from './Report';
// import { ReportService } from './reportService';
// import { useAuth } from '../../../contexts/AuthContext';
// import { ReportPDFGenerator } from './reportPDFGenerator';
// import ReportDetailsDialog from './ReportDetailsDialog';

// const REPORTS_PER_PAGE = 10;

// // Maximum date range in days (90 days = ~3 months)
// const MAX_RANGE_DAYS = 90;

// // Report types that support date filtering
// const DATE_FILTER_SUPPORTED: ReportType[] = [
//   'sales', 'purchase_orders', 'product_performance', 'revenue', 'returns_cancellations',
// ];

// const REPORT_TYPES = [
//   { value: 'sales',                 label: 'Sales Report',                   description: 'Analysis of all sales orders' },
//   { value: 'inventory',             label: 'Inventory Report',               description: 'Current stock levels across warehouses' },
//   { value: 'purchase_orders',       label: 'Purchase Orders Report',         description: 'Supplier purchase order analysis' },
//   { value: 'low_stock',             label: 'Low Stock Alert Report',         description: 'Products requiring restock' },
//   { value: 'warehouse_performance', label: 'Warehouse Performance',          description: 'Warehouse capacity and utilization' },
//   { value: 'product_performance',   label: 'Product Performance',            description: 'Product sales and turnover analysis (delivered orders)' },
//   { value: 'revenue',               label: 'Revenue Report',                 description: 'Daily revenue trends and top performers (delivered orders)' },
//   { value: 'returns_cancellations', label: 'Returns & Cancellations Report', description: 'Returned, cancelled and refunded order analysis' },
// ];

// // ── Theme-aware chip style helper ─────────────────────────────────────────────
// //
// // In light mode: soft pastel tints (low-opacity fills).
// // In dark mode:  richer semi-transparent fills + brighter text so the chip
// //                is legible against dark paper backgrounds.
// //
// // Pass `isDark` (from useTheme) to switch between the two palettes.
// // ─────────────────────────────────────────────────────────────────────────────
// const getSoftChipSx = (color: string, isDark: boolean = false) => {
//   type PaletteEntry = { bg: string; border: string; text: string; iconColor: string };

//   const lightPalette: Record<string, PaletteEntry> = {
//     warning:   { bg: 'rgba(255,167,38,0.12)',  border: 'rgba(255,167,38,0.4)',   text: '#b76e00', iconColor: '#e68900' },
//     info:      { bg: 'rgba(41,182,246,0.12)',   border: 'rgba(41,182,246,0.4)',   text: '#0277bd', iconColor: '#039be5' },
//     primary:   { bg: 'rgba(33,150,243,0.12)',   border: 'rgba(33,150,243,0.4)',   text: '#1565c0', iconColor: '#1976d2' },
//     success:   { bg: 'rgba(102,187,106,0.15)',  border: 'rgba(102,187,106,0.45)', text: '#2e7d32', iconColor: '#43a047' },
//     error:     { bg: 'rgba(239,83,80,0.12)',    border: 'rgba(239,83,80,0.4)',    text: '#c62828', iconColor: '#e53935' },
//     secondary: { bg: 'rgba(171,71,188,0.10)',   border: 'rgba(171,71,188,0.35)',  text: '#6a1b9a', iconColor: '#8e24aa' },
//     default:   { bg: 'rgba(158,158,158,0.12)',  border: 'rgba(158,158,158,0.4)',  text: '#424242', iconColor: '#757575' },
//   };

//   // Dark mode: higher-opacity fills + light/vivid text so chips pop on dark surfaces
//   const darkPalette: Record<string, PaletteEntry> = {
//     warning:   { bg: 'rgba(255,167,38,0.22)',  border: 'rgba(255,167,38,0.55)',  text: '#ffcc80', iconColor: '#ffa726' },
//     info:      { bg: 'rgba(41,182,246,0.20)',   border: 'rgba(41,182,246,0.50)',  text: '#81d4fa', iconColor: '#29b6f6' },
//     primary:   { bg: 'rgba(33,150,243,0.22)',   border: 'rgba(33,150,243,0.50)',  text: '#90caf9', iconColor: '#42a5f5' },
//     success:   { bg: 'rgba(102,187,106,0.22)',  border: 'rgba(102,187,106,0.50)', text: '#a5d6a7', iconColor: '#66bb6a' },
//     error:     { bg: 'rgba(239,83,80,0.22)',    border: 'rgba(239,83,80,0.50)',   text: '#ef9a9a', iconColor: '#ef5350' },
//     secondary: { bg: 'rgba(171,71,188,0.22)',   border: 'rgba(171,71,188,0.50)',  text: '#ce93d8', iconColor: '#ab47bc' },
//     default:   { bg: 'rgba(158,158,158,0.22)',  border: 'rgba(158,158,158,0.50)', text: '#e0e0e0', iconColor: '#bdbdbd' },
//   };

//   const palette = isDark ? darkPalette : lightPalette;
//   const p = palette[color] ?? palette.default;

//   return {
//     backgroundColor: p.bg,
//     borderColor: p.border,
//     color: p.text,
//     fontWeight: 600,
//     border: `1px solid ${p.border}`,
//     '& .MuiChip-icon':  { color: p.iconColor },
//     '& .MuiChip-label': { color: p.text },
//   };
// };

// const getStatusChipColor = (s: string) =>
//   s === 'completed' ? 'success' : s === 'generating' ? 'warning' : s === 'failed' ? 'error' : 'default';

// const getStatusLabel = (s: string) =>
//   s === 'completed' ? 'Completed' : s === 'generating' ? 'Generating' : s === 'failed' ? 'Failed' : s;

// // ── Date helpers ─────────────────────────────────────────────────────────────

// /** YYYY-MM-DD string for today */
// const todayStr = (): string => new Date().toISOString().split('T')[0];

// /** Add `days` to a Date and return YYYY-MM-DD */
// const addDays = (date: Date, days: number): string => {
//   const d = new Date(date.getTime());
//   d.setDate(d.getDate() + days);
//   const today = new Date();
//   if (days > 0 && d > today) return today.toISOString().split('T')[0];
//   return d.toISOString().split('T')[0];
// };

// /** ms difference between two dates in days (b - a) */
// const daysDiff = (a: Date, b: Date): number =>
//   Math.round((b.getTime() - a.getTime()) / 86_400_000);

// // ─────────────────────────────────────────────────────────────────────────────

// // ── LoadingOverlay ────────────────────────────────────────────────────────────
// // Renders a `position: fixed` backdrop that exactly covers the scroll container
// // referenced by `wrapperRef`, regardless of how far the user has scrolled.
// //
// // WHY: A `position: absolute` overlay *inside* a TableContainer (which has
// // overflow:auto) is positioned relative to the element's full scrollable
// // height, not its visible viewport. After scrolling down, the top-anchored
// // overlay moves off screen. Using `position: fixed` + getBoundingClientRect()
// // always covers exactly what is currently visible on screen.
// //
// // DARK MODE: backdrop colour is derived from the MUI theme so it matches the
// // current paper background instead of always being white.
// // ─────────────────────────────────────────────────────────────────────────────
// interface LoadingOverlayProps {
//   wrapperRef: React.RefObject<HTMLDivElement | null>;
// }

// const LoadingOverlay: React.FC<LoadingOverlayProps> = ({ wrapperRef }) => {
//   const [rect, setRect] = useState<DOMRect | null>(null);
//   const theme = useTheme();

//   // Build a semi-transparent version of the theme's paper background so the
//   // overlay always matches whether the app is in light or dark mode.
//   const overlayBg = theme.palette.mode === 'dark'
//     ? 'rgba(26, 29, 36, 0.85)'    // dark paper: #1a1d24 at 85%
//     : 'rgba(255, 255, 255, 0.85)'; // light paper: white at 85%

//   useEffect(() => {
//     const measure = () => {
//       if (wrapperRef.current) {
//         setRect(wrapperRef.current.getBoundingClientRect());
//       }
//     };

//     measure();

//     const el = wrapperRef.current;
//     window.addEventListener('resize', measure);
//     el?.addEventListener('scroll', measure);

//     return () => {
//       window.removeEventListener('resize', measure);
//       el?.removeEventListener('scroll', measure);
//     };
//   }, [wrapperRef]);

//   if (!rect) return null;

//   return (
//     <Box
//       sx={{
//         position: 'fixed',
//         top: rect.top,
//         left: rect.left,
//         width: rect.width,
//         height: rect.height,
//         backgroundColor: overlayBg,
//         backdropFilter: 'blur(2px)',
//         display: 'flex',
//         alignItems: 'center',
//         justifyContent: 'center',
//         zIndex: 1100,
//         pointerEvents: 'all',
//         borderRadius: 'inherit',
//       }}
//     >
//       <Box sx={{ textAlign: 'center' }}>
//         <CircularProgress size={40} />
//         <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
//           Loading reports...
//         </Typography>
//       </Box>
//     </Box>
//   );
// };
// // ─────────────────────────────────────────────────────────────────────────────

// const ReportManagement: React.FC = () => {
//   const { currentUser } = useAuth();

//   const [reports, setReports]               = useState<Report[]>([]);
//   const [loading, setLoading]               = useState(true);
//   const [error, setError]                   = useState('');
//   const [drawerOpen, setDrawerOpen]         = useState(false);
//   const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
//   const [selectedReport, setSelectedReport] = useState<Report | null>(null);
//   const [generating, setGenerating]         = useState(false);
//   const [deleteLoading, setDeleteLoading]   = useState<string>('');
//   const [downloadingPdf, setDownloadingPdf] = useState<string>('');

//   // ── Pagination ───────────────────────────────────────────────────────────
//   const [currentPage, setCurrentPage]     = useState(1);
//   const [totalReports, setTotalReports]   = useState(0);
//   const [lastVisible, setLastVisible]     = useState<any>(null);
//   const [firstVisible, setFirstVisible]   = useState<any>(null);

//   // ── Stats ────────────────────────────────────────────────────────────────
//   const [totalCompleted, setTotalCompleted]   = useState(0);
//   const [totalGenerating, setTotalGenerating] = useState(0);
//   const [totalFailed, setTotalFailed]         = useState(0);

//   // ── Filters ──────────────────────────────────────────────────────────────
//   const [searchInput, setSearchInput]   = useState('');
//   const [searchQuery, setSearchQuery]   = useState('');
//   const [typeFilter, setTypeFilter]     = useState<string>('all');
//   const [statusFilter, setStatusFilter] = useState<string>('all');
//   const [showFilters, setShowFilters]   = useState(false);
//   const [dateError, setDateError]       = useState('');

//   // ── Delete confirmation dialog ────────────────────────────────────────────
//   const [deleteTarget, setDeleteTarget]               = useState<Report | null>(null);
//   const [deleteDialogLoading, setDeleteDialogLoading] = useState(false);

//   const containerRef   = useRef<HTMLDivElement>(null);

//   // Ref for the TableContainer scroll wrapper. Passed to LoadingOverlay so it
//   // can paint a fixed-position backdrop that always covers the full visible
//   // area of the table — even when the user has scrolled down.
//   const tableWrapperRef = useRef<HTMLDivElement>(null);

//   const theme    = useTheme();
//   const isDark   = theme.palette.mode === 'dark';
//   const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

//   const [formData, setFormData] = useState<ReportFormData>({
//     reportType: 'sales', reportName: '', description: '', filters: {},
//   });
//   const [filters, setFilters] = useState<ReportFilters>({});

//   // Does the currently selected report type support date filtering?
//   const showDateFilter = DATE_FILTER_SUPPORTED.includes(formData.reportType as ReportType);

//   // ── Initial load ─────────────────────────────────────────────────────────
//   useEffect(() => {
//     fetchReports(1, 'first', '', 'all', 'all');
//     fetchReportStats();
//   }, []);

//   // Clear dates whenever switching to a report type that doesn't use them
//   useEffect(() => {
//     if (!showDateFilter) {
//       setFilters(prev => ({ ...prev, dateFrom: undefined, dateTo: undefined }));
//       setDateError('');
//     }
//   }, [formData.reportType, showDateFilter]);

//   // ── Fetch paginated page data ─────────────────────────────────────────────
//   //
//   // KEY FIX: The original code read `lastVisible` / `firstVisible` directly
//   // from the React state closure inside fetchReports, which meant that calls
//   // made immediately after setState (e.g. in filter handlers) always saw the
//   // *stale* previous cursor value.
//   //
//   // Solution: accept explicit `cursorAfter` / `cursorBefore` parameters so
//   // callers can pass `null` when they want a true page-1 fetch, or pass the
//   // current cursor values for next/prev navigation.
//   // ─────────────────────────────────────────────────────────────────────────
//   const fetchReports = async (
//     page: number,
//     direction: 'next' | 'prev' | 'first' = 'first',
//     search = '',
//     type = 'all',
//     status = 'all',
//     cursorAfter: any = null,
//     cursorBefore: any = null,
//   ) => {
//     try {
//       setLoading(true);
//       setError('');

//       const result = await ReportService.getReportsPaginated(
//         REPORTS_PER_PAGE,
//         direction === 'next' ? cursorAfter  : null,
//         direction === 'prev' ? cursorBefore : null,
//         direction,
//         search,
//         type   !== 'all' ? (type   as ReportType) : undefined,
//         status !== 'all' ? (status as 'completed' | 'generating' | 'failed') : undefined,
//       );

//       setReports(result.reports);
//       setTotalReports(result.totalCount);
//       setLastVisible(result.lastVisible);
//       setFirstVisible(result.firstVisible);
//       setCurrentPage(page);
//     } catch (err: any) {
//       setError(err.message || 'Failed to fetch reports');
//     } finally {
//       setLoading(false);
//     }
//   };

//   // ── Core helper: reset pagination cursors then fetch page 1 ──────────────
//   //
//   // Call this any time you want to go back to page 1 with fresh cursors
//   // (after generate / delete / filter change / search / refresh).
//   // Synchronously clears cursor state and passes null explicitly so
//   // fetchReports always gets null cursors and truly fetches the first page.
//   // ──────────────────────────────────────────────────────────────────────────
//   const resetAndFetchFirstPage = async (
//     search: string = searchQuery,
//     type: string   = typeFilter,
//     status: string = statusFilter,
//   ) => {
//     setCurrentPage(1);
//     setLastVisible(null);
//     setFirstVisible(null);

//     // Pass null cursors explicitly — do NOT rely on the state values above
//     // since setState is async and fetchReports would still see the old values.
//     await fetchReports(1, 'first', search, type, status, null, null);
//   };

//   const fetchReportStats = async () => {
//     try {
//       const stats = await ReportService.getReportStats();
//       setTotalCompleted(stats.completed);
//       setTotalGenerating(stats.generating);
//       setTotalFailed(stats.failed);
//     } catch {}
//   };

//   // ── Pagination: pass current cursor values explicitly so the closure always
//   // reads the up-to-date cursor, not a potentially-stale state snapshot.
//   const handlePageChange = (_: React.ChangeEvent<unknown>, page: number) => {
//     if      (page > currentPage) fetchReports(page, 'next',  searchQuery, typeFilter, statusFilter, lastVisible,  firstVisible);
//     else if (page < currentPage) fetchReports(page, 'prev',  searchQuery, typeFilter, statusFilter, lastVisible,  firstVisible);
//     else                          fetchReports(page, 'first', searchQuery, typeFilter, statusFilter, null, null);
//   };

//   const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
//     if (e.key === 'Enter') {
//       e.preventDefault();
//       setSearchQuery(searchInput);
//       // ── FIX: reset cursors before fetching so search always starts at page 1
//       resetAndFetchFirstPage(searchInput, typeFilter, statusFilter);
//     }
//   };

//   // ── FIX: filter handlers previously called fetchReports immediately after
//   // setState which read stale cursor values. Now delegate to
//   // resetAndFetchFirstPage which passes null cursors explicitly.
//   const handleTypeFilterChange = (v: string) => {
//     setTypeFilter(v);
//     resetAndFetchFirstPage(searchQuery, v, statusFilter);
//   };

//   const handleStatusFilterChange = (v: string) => {
//     setStatusFilter(v);
//     resetAndFetchFirstPage(searchQuery, typeFilter, v);
//   };

//   const clearFilters = () => {
//     setSearchInput('');
//     setSearchQuery('');
//     setTypeFilter('all');
//     setStatusFilter('all');
//     // ── FIX: reset cursors so clearing filters always fetches a true page 1
//     resetAndFetchFirstPage('', 'all', 'all');
//   };

//   const hasActiveFilters   = searchQuery !== '' || typeFilter !== 'all' || statusFilter !== 'all';
//   const activeFiltersCount = [searchQuery !== '', typeFilter !== 'all', statusFilter !== 'all'].filter(Boolean).length;

//   const resetForm = () => {
//     setFormData({ reportType: 'sales', reportName: '', description: '', filters: {} });
//     setFilters({});
//     setDateError('');
//   };

//   const handleAddReport   = () => { resetForm(); setDrawerOpen(true); };
//   const handleCloseDrawer = () => { setDrawerOpen(false); resetForm(); };
//   const handleViewReport  = (report: Report) => { setSelectedReport(report); setDetailsDialogOpen(true); };

//   // ── Delete ────────────────────────────────────────────────────────────────
//   const handleDeleteReport  = (report: Report) => setDeleteTarget(report);
//   const handleDeleteCancel  = () => setDeleteTarget(null);

//   const handleDeleteConfirm = async () => {
//     if (!deleteTarget) return;
//     try {
//       setDeleteDialogLoading(true);
//       setDeleteLoading(deleteTarget.id!);
//       await ReportService.deleteReport(deleteTarget.id!);
//       setDeleteTarget(null);
//       // ── FIX: was calling fetchReports(currentPage, ...) with stale cursors.
//       // Reset to page 1 so delete always shows the latest first page.
//       await resetAndFetchFirstPage();
//       await fetchReportStats();
//     } catch (err: any) {
//       alert('Failed to delete report: ' + (err.message || 'Unknown error'));
//     } finally {
//       setDeleteDialogLoading(false);
//       setDeleteLoading('');
//     }
//   };
//   // ──────────────────────────────────────────────────────────────────────────

//   const handleGenerateReport = async (e: React.FormEvent) => {
//     e.preventDefault();
//     if (!formData.reportName.trim()) { alert('Report name is required'); return; }
//     if (dateError) { alert('Please fix the date range error before generating.'); return; }
//     try {
//       setGenerating(true);
//       await ReportService.generateReport(
//         formData.reportType, formData.reportName, formData.description, filters,
//         currentUser?.uid || '', currentUser?.displayName || currentUser?.email || 'Unknown User',
//       );
//       // ── FIX: was calling fetchReports(currentPage, ...) with stale cursors.
//       // Reset to page 1 after generation so the new report appears at the top.
//       await resetAndFetchFirstPage();
//       await fetchReportStats();
//       setDrawerOpen(false);
//       resetForm();
//     } catch (err: any) {
//       alert('Failed to generate report: ' + (err.message || 'Unknown error'));
//     } finally {
//       setGenerating(false);
//     }
//   };

//   const handleDownloadPDF = async (report: Report) => {
//     try {
//       setDownloadingPdf(report.id!);
//       await ReportPDFGenerator.generatePDF(report);
//     } catch (err: any) {
//       alert('Failed to download PDF: ' + (err.message || 'Unknown error'));
//     } finally {
//       setDownloadingPdf('');
//     }
//   };

//   // ── Refresh button ────────────────────────────────────────────────────────
//   const handleRefresh = async () => {
//     // ── FIX: was calling fetchReports(currentPage, ...) with stale cursors.
//     // Reset to page 1 so refresh always shows the latest first page.
//     await resetAndFetchFirstPage();
//     await fetchReportStats();
//   };

//   // ── Date handlers ─────────────────────────────────────────────────────────
//   const handleDateFromChange = (value: string) => {
//     if (!value) {
//       setFilters(prev => ({ ...prev, dateFrom: undefined }));
//       setDateError('');
//       return;
//     }
//     const newFrom = new Date(value);
//     setFilters(prev => {
//       if (prev.dateTo) {
//         const diff = daysDiff(newFrom, prev.dateTo);
//         if (diff < 0)              setDateError('Start date cannot be after end date.');
//         else if (diff > MAX_RANGE_DAYS) setDateError(`Date range cannot exceed ${MAX_RANGE_DAYS} days.`);
//         else                       setDateError('');
//       } else {
//         setDateError('');
//       }
//       return { ...prev, dateFrom: newFrom };
//     });
//   };

//   const handleDateToChange = (value: string) => {
//     if (!value) {
//       setFilters(prev => ({ ...prev, dateTo: undefined }));
//       setDateError('');
//       return;
//     }
//     const newTo = new Date(value);
//     setFilters(prev => {
//       if (prev.dateFrom) {
//         const diff = daysDiff(prev.dateFrom, newTo);
//         if (diff < 0)              setDateError('End date cannot be before start date.');
//         else if (diff > MAX_RANGE_DAYS) setDateError(`Date range cannot exceed ${MAX_RANGE_DAYS} days.`);
//         else                       setDateError('');
//       } else {
//         setDateError('');
//       }
//       return { ...prev, dateTo: newTo };
//     });
//   };

//   const dateFromValue = filters.dateFrom ? filters.dateFrom.toISOString().split('T')[0] : '';
//   const dateToValue   = filters.dateTo   ? filters.dateTo.toISOString().split('T')[0]   : '';

//   const dateFromMin = filters.dateTo   ? addDays(filters.dateTo,   -MAX_RANGE_DAYS) : undefined;
//   const dateFromMax = filters.dateTo   ? dateToValue                                : todayStr();
//   const dateToMin   = filters.dateFrom ? dateFromValue                              : undefined;
//   const dateToMax   = filters.dateFrom ? addDays(filters.dateFrom,  MAX_RANGE_DAYS) : todayStr();

//   const getReportTypeLabel = (type: ReportType) =>
//     REPORT_TYPES.find(rt => rt.value === type)?.label || type;

//   const totalPages = Math.ceil(totalReports / REPORTS_PER_PAGE);

//   if (loading && reports.length === 0) {
//     return (
//       <Box display="flex" alignItems="center" justifyContent="center" minHeight="400px">
//         <CircularProgress size={40} />
//         <Typography variant="body1" sx={{ ml: 2 }}>Loading reports...</Typography>
//       </Box>
//     );
//   }

//   return (
//     <Box
//       ref={containerRef}
//       sx={{
//         height: { md: '100%' },
//         display: 'flex',
//         flexDirection: 'column',
//         overflow: { xs: 'visible', md: 'hidden' },
//         p: { xs: 2, sm: 3 },
//       }}
//     >
//       {/* ── Header ── */}
//       <Box sx={{ flexShrink: 0 }}>
//         <Stack
//           direction={{ xs: 'column', sm: 'row' }}
//           justifyContent="space-between"
//           alignItems={{ xs: 'flex-start', sm: 'center' }}
//           spacing={2}
//           sx={{ mb: 3 }}
//         >
//           <Stack direction="row" alignItems="center" spacing={2}>
//             <AssessmentIcon sx={{ fontSize: { xs: 28, md: 32 }, color: 'primary.main' }} />
//             <Box>
//               <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.5rem', md: '2rem' } }}>
//                 Report Management
//               </Typography>
//               <Typography variant="body2" color="text.secondary">
//                 Generate and manage business reports
//               </Typography>
//             </Box>
//           </Stack>
//           <Stack direction="row" spacing={1} sx={{ alignSelf: { xs: 'flex-start', sm: 'auto' } }}>
//             <Button
//               onClick={handleRefresh}
//               variant="outlined"
//               startIcon={<RefreshIcon />}
//               size={isMobile ? 'small' : 'medium'}
//               disabled={loading}
//             >
//               {isMobile ? '' : 'Refresh'}
//             </Button>
//             <Button
//               onClick={handleAddReport}
//               variant="contained"
//               startIcon={<AddIcon />}
//               size={isMobile ? 'small' : 'medium'}
//             >
//               Generate Report
//             </Button>
//           </Stack>
//         </Stack>

//         {error && (
//           <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
//             {error}
//           </Alert>
//         )}

//         {/* ── Stats Cards ── */}
//         <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }} sx={{ mb: 3 }} alignItems="stretch">
//           {[
//             {
//               label: 'Total Reports',
//               value: totalReports,
//               sub: (
//                 <>
//                   <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
//                     <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'success.main', flexShrink: 0 }} />
//                     <Typography variant="caption" color="success.main" noWrap>{totalCompleted} completed</Typography>
//                   </Box>
//                   <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
//                     <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'error.main', flexShrink: 0 }} />
//                     <Typography variant="caption" color="error.main" noWrap>{totalFailed} failed</Typography>
//                   </Box>
//                 </>
//               ),
//             },
//             {
//               label: 'Completed',
//               value: totalCompleted,
//               sub: <Typography variant="caption" color="text.secondary" noWrap>{totalReports > 0 ? `${((totalCompleted / totalReports) * 100).toFixed(1)}% of total` : '—'}</Typography>,
//             },
//             {
//               label: 'Generating',
//               value: totalGenerating,
//               sub: <Typography variant="caption" color="text.secondary" noWrap>{totalGenerating > 0 ? 'currently in progress' : 'none in progress'}</Typography>,
//             },
//             {
//               label: 'Failed',
//               value: totalFailed,
//               sub: <Typography variant="caption" color="text.secondary" noWrap>{totalReports > 0 ? `${((totalFailed / totalReports) * 100).toFixed(1)}% failure rate` : '—'}</Typography>,
//             },
//           ].map((card, i) => (
//             <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }} key={i}>
//               <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
//                 <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } }, flex: 1, display: 'flex', flexDirection: 'column' }}>
//                   <Typography color="text.secondary" gutterBottom variant="body2" noWrap>{card.label}</Typography>
//                   <Typography variant="h5" component="div" color="text.primary" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' }, mb: 1 }}>
//                     {card.value}
//                   </Typography>
//                   <Box sx={{ mt: 'auto', display: 'flex', flexDirection: 'column', gap: 0.5 }}>{card.sub}</Box>
//                 </CardContent>
//               </Card>
//             </Grid>
//           ))}
//         </Grid>

//         {/* ── Search & Filters ── */}
//         <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 2 }}>
//           <Stack
//             direction={{ xs: 'column', sm: 'row' }}
//             spacing={2}
//             alignItems={{ xs: 'stretch', sm: 'center' }}
//             justifyContent="space-between"
//           >
//             <TextField
//               placeholder="Search reports by name... (Press Enter)"
//               value={searchInput}
//               onChange={e => setSearchInput(e.target.value)}
//               onKeyPress={handleSearchKeyPress}
//               sx={{ flexGrow: 1, maxWidth: { sm: 400 } }}
//               size="small"
//               InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
//             />
//             <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
//               <Button
//                 onClick={() => setShowFilters(!showFilters)}
//                 variant={hasActiveFilters ? 'contained' : 'outlined'}
//                 startIcon={<FilterListIcon />}
//                 endIcon={showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
//                 size="small"
//               >
//                 Filters {hasActiveFilters && `(${activeFiltersCount})`}
//               </Button>
//               {hasActiveFilters && (
//                 <Button onClick={clearFilters} variant="text" size="small" startIcon={<ClearIcon />}>
//                   Clear all
//                 </Button>
//               )}
//             </Stack>
//           </Stack>

//           <Collapse in={showFilters}>
//             <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
//               <Grid container spacing={2}>
//                 <Grid item xs={12} sm={6} md={3}>
//                   <FormControl fullWidth size="small">
//                     <InputLabel>Report Type</InputLabel>
//                     <Select value={typeFilter} onChange={e => handleTypeFilterChange(e.target.value)} label="Report Type">
//                       <MenuItem value="all">All Types</MenuItem>
//                       {REPORT_TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
//                     </Select>
//                   </FormControl>
//                 </Grid>
//                 <Grid item xs={12} sm={6} md={3}>
//                   <FormControl fullWidth size="small">
//                     <InputLabel>Status</InputLabel>
//                     <Select value={statusFilter} onChange={e => handleStatusFilterChange(e.target.value)} label="Status">
//                       <MenuItem value="all">All Status</MenuItem>
//                       <MenuItem value="completed">Completed</MenuItem>
//                       <MenuItem value="generating">Generating</MenuItem>
//                       <MenuItem value="failed">Failed</MenuItem>
//                     </Select>
//                   </FormControl>
//                 </Grid>
//                 <Grid item xs={12} md={6}>
//                   <Box sx={{ display: 'flex', alignItems: 'center', height: '40px' }}>
//                     <Typography variant="body2" color="text.secondary">
//                       {hasActiveFilters
//                         ? `Found ${reports.length} reports matching your filters`
//                         : `Showing ${reports.length} reports on this page`}
//                     </Typography>
//                   </Box>
//                 </Grid>
//               </Grid>
//             </Box>
//           </Collapse>
//         </Paper>
//       </Box>

//       {/* ── Scrollable Body ── */}
//       <Box sx={{ flex: 1, overflow: { xs: 'visible', md: 'auto' }, display: 'flex', flexDirection: 'column', minHeight: { xs: 'auto', md: 0 } }}>

//         {reports.length === 0 && !loading && (
//           <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
//             <Card sx={{ width: '100%', maxWidth: 500 }}>
//               <CardContent sx={{ textAlign: 'center', py: 6 }}>
//                 <AssessmentIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
//                 <Typography variant="h5" color="text.secondary" gutterBottom>
//                   {hasActiveFilters ? 'No Reports Match Your Filters' : 'No Reports Generated'}
//                 </Typography>
//                 <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}>
//                   {hasActiveFilters
//                     ? 'Try adjusting your search or filter criteria.'
//                     : 'Start by generating your first business report.'}
//                 </Typography>
//                 {hasActiveFilters
//                   ? <Button onClick={clearFilters} variant="outlined" startIcon={<ClearIcon />}>Clear Filters</Button>
//                   : <Button onClick={handleAddReport} variant="contained" startIcon={<AddIcon />}>Generate Report</Button>}
//               </CardContent>
//             </Card>
//           </Box>
//         )}

//         {reports.length > 0 && (
//           <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: { xs: 'auto', md: 0 } }}>

//             {/*
//               ── Scrollable table wrapper ──────────────────────────────────────
//               `ref={tableWrapperRef}` is placed directly on TableContainer.
//               LoadingOverlay calls getBoundingClientRect() on this node and
//               renders a fixed-position backdrop that covers exactly the visible
//               area — regardless of how far the user has scrolled.

//               The overlay is rendered OUTSIDE this TableContainer (just below)
//               so it is never clipped by overflow:auto.

//               The old `position:absolute` overlay that lived *inside* the
//               TableContainer has been removed — it was clipped by overflow:auto
//               and only covered the initially-visible portion when scrolled.
//               ─────────────────────────────────────────────────────────────────
//             */}
//             <TableContainer
//               component={Paper}
//               ref={tableWrapperRef}
//               sx={{
//                 flex: 1,
//                 overflow: { xs: 'auto', md: 'auto' },
//                 maxHeight: { md: '100%' },
//                 mb: 2,
//                 position: 'relative',
//                 '& .MuiTable-root': { minWidth: { xs: 650, md: 1000 } },
//               }}
//             >
//               <Table stickyHeader size={isMobile ? 'small' : 'medium'}>
//                 <TableHead>
//                   <TableRow>
//                     <TableCell>Report Name</TableCell>
//                     <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Type</TableCell>
//                     <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Generated By</TableCell>
//                     <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Generated At</TableCell>
//                     <TableCell>Status</TableCell>
//                     <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Records</TableCell>
//                     <TableCell align="right">Actions</TableCell>
//                   </TableRow>
//                 </TableHead>
//                 <TableBody>
//                   {reports.map(report => (
//                     <TableRow
//                       key={report.id}
//                       hover
//                       sx={{ '&:last-child td, &:last-child th': { borderBottom: 0 } }}
//                     >
//                       <TableCell>
//                         <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: { xs: '0.85rem', sm: '1rem' } }}>
//                           {report.reportName}
//                         </Typography>
//                         {report.description && (
//                           <Typography variant="caption" color="text.secondary" display="block">
//                             {report.description}
//                           </Typography>
//                         )}
//                         <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'block', sm: 'none' } }}>
//                           {getReportTypeLabel(report.reportType)}
//                         </Typography>
//                       </TableCell>

//                       {/* Type chip — isDark passed so chip uses dark-mode palette */}
//                       <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
//                         <Chip
//                           label={getReportTypeLabel(report.reportType)}
//                           size="small"
//                           variant="outlined"
//                           sx={getSoftChipSx('primary', isDark)}
//                         />
//                       </TableCell>

//                       <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
//                         <Typography variant="body2">{report.generatedByName}</Typography>
//                       </TableCell>

//                       <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
//                         <Typography variant="body2" color="text.secondary">
//                           {report.generatedAt
//                             ? new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'short', day: 'numeric' }).format(report.generatedAt)
//                             : '-'}
//                         </Typography>
//                       </TableCell>

//                       {/* Status chip — isDark passed so chip uses dark-mode palette */}
//                       <TableCell>
//                         <Chip
//                           label={getStatusLabel(report.status)}
//                           size="small"
//                           variant="outlined"
//                           sx={getSoftChipSx(getStatusChipColor(report.status), isDark)}
//                         />
//                       </TableCell>

//                       <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
//                         <Typography variant="body2">{report.summary?.totalRecords || 0}</Typography>
//                       </TableCell>

//                       <TableCell align="right">
//                         <Stack direction="row" spacing={0.5} justifyContent="flex-end" flexWrap="nowrap">
//                           {report.status === 'completed' && (
//                             <>
//                               <Tooltip title="View Details">
//                                 <IconButton onClick={() => handleViewReport(report)} size="small" color="primary">
//                                   <VisibilityIcon fontSize="small" />
//                                 </IconButton>
//                               </Tooltip>
//                               <Tooltip title="Download PDF">
//                                 <IconButton
//                                   onClick={() => handleDownloadPDF(report)}
//                                   disabled={downloadingPdf === report.id}
//                                   size="small"
//                                   color="secondary"
//                                 >
//                                   {downloadingPdf === report.id
//                                     ? <CircularProgress size={16} />
//                                     : <PdfIcon fontSize="small" />}
//                                 </IconButton>
//                               </Tooltip>
//                             </>
//                           )}
//                           <Tooltip title="Delete">
//                             <IconButton
//                               onClick={() => handleDeleteReport(report)}
//                               disabled={deleteLoading === report.id}
//                               size="small"
//                               color="error"
//                             >
//                               {deleteLoading === report.id
//                                 ? <CircularProgress size={16} />
//                                 : <DeleteIcon fontSize="small" />}
//                             </IconButton>
//                           </Tooltip>
//                         </Stack>
//                       </TableCell>
//                     </TableRow>
//                   ))}
//                 </TableBody>
//               </Table>
//             </TableContainer>

//             {/*
//               Rendered OUTSIDE the TableContainer so it is never clipped by
//               overflow:auto. Measures the TableContainer via tableWrapperRef
//               and paints itself at the correct fixed screen coordinates.
//               Dark-mode aware via useTheme() inside LoadingOverlay.
//             */}
//             {loading && <LoadingOverlay wrapperRef={tableWrapperRef} />}

//             {/* Pagination */}
//             <Paper elevation={2} sx={{ flexShrink: 0, borderRadius: 1 }}>
//               <Box sx={{ py: 2, px: 2 }}>
//                 <Stack
//                   direction={{ xs: 'column', sm: 'row' }}
//                   spacing={{ xs: 1.5, sm: 3 }}
//                   alignItems="center"
//                   justifyContent="space-between"
//                   sx={{ width: '100%' }}
//                 >
//                   <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
//                     Showing {totalReports === 0 ? 0 : (currentPage - 1) * REPORTS_PER_PAGE + 1}–
//                     {Math.min(currentPage * REPORTS_PER_PAGE, totalReports)} of {totalReports} reports
//                   </Typography>
//                   <Pagination
//                     count={Math.max(1, totalPages)}
//                     page={currentPage}
//                     onChange={handlePageChange}
//                     color="primary"
//                     size={isMobile ? 'small' : 'medium'}
//                     showFirstButton
//                     showLastButton
//                     disabled={loading || totalPages <= 1}
//                   />
//                   <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
//                     Page {currentPage} of {Math.max(1, totalPages)}
//                   </Typography>
//                 </Stack>
//               </Box>
//             </Paper>
//           </Box>
//         )}
//       </Box>

//       {/* ── Generate Report Drawer ── */}
//       <Drawer
//         anchor="right"
//         open={drawerOpen}
//         onClose={handleCloseDrawer}
//         PaperProps={{ sx: { width: { xs: '100%', sm: 560 } } }}
//       >
//         <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>

//           {/* Header */}
//           <Box
//             sx={{
//               px: 3, py: 2,
//               borderBottom: 1, borderColor: 'divider',
//               display: 'flex', alignItems: 'center', justifyContent: 'space-between',
//               flexShrink: 0,
//             }}
//           >
//             <Typography variant="h6">Generate New Report</Typography>
//             <IconButton onClick={handleCloseDrawer} edge="end"><CloseIcon /></IconButton>
//           </Box>

//           {/* Form */}
//           <Box
//             component="form"
//             onSubmit={handleGenerateReport}
//             sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}
//           >
//             <Box
//               sx={{
//                 flex: 1, overflow: 'auto', px: 3, py: 3, minHeight: 0,
//                 '&::-webkit-scrollbar': { width: '6px' },
//                 '&::-webkit-scrollbar-track': { background: 'transparent' },
//                 '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.2)', borderRadius: '3px' },
//                 '&::-webkit-scrollbar-thumb:hover': { background: 'rgba(0,0,0,0.35)' },
//               }}
//             >
//               <Grid container spacing={3}>

//                 {/* Report Type */}
//                 <Grid item xs={12}>
//                   <FormControl fullWidth>
//                     <InputLabel>Report Type</InputLabel>
//                     <Select
//                       value={formData.reportType}
//                       label="Report Type"
//                       onChange={e => setFormData(prev => ({ ...prev, reportType: e.target.value as ReportType }))}
//                     >
//                       {REPORT_TYPES.map(t => (
//                         <MenuItem key={t.value} value={t.value}>
//                           <Box>
//                             <Typography variant="body1">{t.label}</Typography>
//                             <Typography variant="caption" color="text.secondary">{t.description}</Typography>
//                           </Box>
//                         </MenuItem>
//                       ))}
//                     </Select>
//                   </FormControl>
//                 </Grid>

//                 {/* Report Name */}
//                 <Grid item xs={12}>
//                   <TextField
//                     fullWidth label="Report Name" required
//                     value={formData.reportName}
//                     onChange={e => setFormData(prev => ({ ...prev, reportName: e.target.value }))}
//                     placeholder="e.g., Monthly Sales Report - February 2026"
//                   />
//                 </Grid>

//                 {/* Description */}
//                 <Grid item xs={12}>
//                   <TextField
//                     fullWidth label="Description" multiline rows={2}
//                     value={formData.description}
//                     onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
//                     placeholder="Optional description for this report"
//                   />
//                 </Grid>

//                 {/* Date filter section — only shown for relevant report types */}
//                 {showDateFilter && (
//                   <>
//                     <Grid item xs={12}>
//                       <Divider sx={{ mb: 2 }} />
//                       <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
//                         <FilterListIcon color="primary" fontSize="small" />
//                         <Typography variant="subtitle1" fontWeight={600}>Date Filter</Typography>
//                       </Stack>
//                       <Typography variant="caption" color="text.secondary">
//                         Maximum range: 90 days. Leave blank to include all dates.
//                       </Typography>
//                     </Grid>

//                     <Grid item xs={12} sm={6}>
//                       <TextField
//                         fullWidth label="Date From" type="date"
//                         value={dateFromValue}
//                         onChange={e => handleDateFromChange(e.target.value)}
//                         InputLabelProps={{ shrink: true }}
//                         inputProps={{ min: dateFromMin, max: dateFromMax }}
//                         error={!!dateError}
//                       />
//                     </Grid>

//                     <Grid item xs={12} sm={6}>
//                       <TextField
//                         fullWidth label="Date To" type="date"
//                         value={dateToValue}
//                         onChange={e => handleDateToChange(e.target.value)}
//                         InputLabelProps={{ shrink: true }}
//                         inputProps={{ min: dateToMin, max: dateToMax }}
//                         error={!!dateError}
//                       />
//                     </Grid>

//                     {dateError && (
//                       <Grid item xs={12}>
//                         <Alert severity="error" sx={{ py: 0.5 }}>{dateError}</Alert>
//                       </Grid>
//                     )}
//                   </>
//                 )}

//               </Grid>
//             </Box>

//             {/* Footer */}
//             <Divider />
//             <Box sx={{ px: 3, py: 2, display: 'flex', gap: 2, justifyContent: 'flex-end', flexShrink: 0 }}>
//               <Button onClick={handleCloseDrawer} disabled={generating} startIcon={<CancelIcon />}>
//                 Cancel
//               </Button>
//               <Button
//                 type="submit"
//                 variant="contained"
//                 disabled={generating || !!dateError}
//                 startIcon={generating ? <CircularProgress size={16} /> : <AssessmentIcon />}
//               >
//                 {generating ? 'Generating...' : 'Generate Report'}
//               </Button>
//             </Box>
//           </Box>
//         </Box>
//       </Drawer>

//       {/* ── Delete Confirmation Dialog ── */}
//       <Dialog open={!!deleteTarget} onClose={handleDeleteCancel} maxWidth="xs" fullWidth>
//         <DialogTitle>Delete Report</DialogTitle>
//         <DialogContent>
//           <DialogContentText>
//             Are you sure you want to delete{' '}
//             <strong>"{deleteTarget?.reportName}"</strong>?{' '}
//             This action cannot be undone.
//           </DialogContentText>
//         </DialogContent>
//         <DialogActions sx={{ px: 3, pb: 2 }}>
//           <Button onClick={handleDeleteCancel} disabled={deleteDialogLoading}>
//             Cancel
//           </Button>
//           <Button
//             variant="contained"
//             color="error"
//             onClick={handleDeleteConfirm}
//             disabled={deleteDialogLoading}
//             startIcon={deleteDialogLoading ? <CircularProgress size={16} /> : <DeleteIcon />}
//           >
//             Delete
//           </Button>
//         </DialogActions>
//       </Dialog>

//       {selectedReport && (
//         <ReportDetailsDialog
//           open={detailsDialogOpen}
//           report={selectedReport}
//           onClose={() => { setDetailsDialogOpen(false); setSelectedReport(null); }}
//         />
//       )}
//     </Box>
//   );
// };

// export default ReportManagement;